import React, { useRef, useState, useEffect, useMemo } from 'react';
import BreadCrumbs from './BreadCrumbs';
import HeaderComponent from './HeaderComponent';
import FeatureNav from './FeatureNav';
import FooterSection from './FooterSection';
import "react-tabulator/css/tabulator.min.css";
import { Tabulator, FormatModule, DownloadModule, PageModule, ExportModule } from "tabulator-tables";
import "tabulator-tables/dist/css/tabulator.min.css";
import { EmployeeData, Filters } from 'types/employee';
import EmployeeTable  from './EmployeeEmployeeTable';
import * as d3 from "d3";
import Loader from './Loader';
import 'bootstrap-select/dist/css/bootstrap-select.min.css';
import 'bootstrap-select';
import { Collapse} from 'react-bootstrap';
import { MultiSelectAsync } from './MultiSelectAsync';
import ErrorModal from 'utils/ErrorModal';

Tabulator.registerModule([FormatModule, DownloadModule, PageModule, ExportModule]);

const EmployeeEmployeePaySearch = () => {
  const [searchName, setSearchName] = useState<string>("");
  const [data, setData] = useState<EmployeeData[]>([]);
  const [filters, setFilters] = useState<Filters>({
    filterYear: [],
    filterEntity: [],
    filterEmployee: [],
    filterTitle: [],
  });
  const [selectedFilteredYears, setSelectedFilteredYears] = useState<string[]>([]);
  const [selectedFilteredEntities, setSelectedFilteredEntities] = useState<string[]>([]);
  const [selectedFilteredEmployees, setSelectedFilteredEmployees] = useState<string[]>([]);
  const [selectedFilteredTitles, setSelectedFilteredTitles] = useState<string[]>([]);
  const [error, setError] =  useState<string>("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showHeaderInfo, setShowHeaderInfo] = useState<boolean>(false);
  const tabulatorRef = useRef<InstanceType<typeof Tabulator> | null>(null);

  const getEmployeePayInfo = async () => {
    setLoading(true);
    try {
      setFilters({ 
        filterYear: [], 
        filterEntity: [],
        filterEmployee: [], 
        filterTitle: [],
      })
      const queryFunc = "getEmployeeSearch";
      const queryParam = { name: searchName };

      const response = await fetch("https://tu-query-handler-prod-uewlhjwsua-wm.a.run.app", {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: `function=${encodeURIComponent(queryFunc)}&parameter=${encodeURIComponent(JSON.stringify(queryParam))}`
      });

      if (!response.ok) {
        throw new Error(`Network response was ${response.status}`);
      }

      const data = await response.json();
      setData(data);

      if (!data || data.length === 0) {
        setError("No data found that matches your search parameters. Please make another search query.");
        setShowErrorModal(true);
      }
      const employeFilterYears = getEmployeeFilterYears({ data: data });
      const employeeFilterEntities = getEmployeeFilterEntities({ data: data });
      const employeeFilterEmployees = getEmployeeFilterEmployees({ data: data });
      const employeeFilterTitles = getEmployeeFilterTitles({ data: data });
      setFilters({ 
        filterYear: employeFilterYears, 
        filterEntity: employeeFilterEntities,
        filterEmployee: employeeFilterEmployees, 
        filterTitle: employeeFilterTitles,
      })
      setLoading(false);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getEmployeeFilterYears = ({ data } : { data: EmployeeData[] }) => {
    const filteredYears = data.map((item) => item['fiscal_year']);
    const filteredUniqueYears = [...new Set(filteredYears)].sort(); 
    return filteredUniqueYears;
  }

  const getEmployeeFilterEntities = ({ data } : { data: EmployeeData[] }) => {
    const filteredEntities = data.map((item) => item['entity_name']);
    const uinqueEntities = Array.from(new Set(filteredEntities));
    const sortedEntities = uinqueEntities.sort();
    return sortedEntities;
  }

  const getEmployeeFilterEmployees = ({ data } : { data: EmployeeData[] }) => {
    const filteredEmployees = data.map((item) => item['employee_name']);
    const uniqueEmployees = Array.from(new Set(filteredEmployees));
    const sortedEmployees = uniqueEmployees.sort();
    return sortedEmployees;
  }

  const getEmployeeFilterTitles = ({ data } : { data: EmployeeData[] }) => {
    const filteredTitles = data.map((item) => item['title']);
    const uniqueTitles = Array.from(new Set(filteredTitles));
    const sortedTitles = uniqueTitles.sort();
    return sortedTitles;
  }

  const handleDownload = (type: "csv" | "xlsx" | "html" | "json") => {
    if (!tabulatorRef.current) {
      return;
    }

    switch (type) {
      case "csv":
        tabulatorRef.current.download("csv", "data.csv");
        break;
      case "xlsx":
        tabulatorRef.current.download("xlsx", "data.xlsx", { sheetName: "Data" });
        break;
      case "html":
        tabulatorRef.current.download("html", "data.html", { style: true });
        break;
      case "json":
        tabulatorRef.current.download("json", "data.json");
        break;
    }
  };

  // Updated D3 Chart Implementation with Download Capability
  const EmployeeChart: React.FC<{ data: EmployeeData[] }> = ({ data }) => {
    const chartRef = useRef<SVGSVGElement | null>(null);

    // Download current D3 chart as PNG
    const handleD3ChartDownload = (svgNode: SVGSVGElement) => {
      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(svgNode);
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);

      const img = new Image();
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        context?.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);

        const pngUrl = canvas.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = pngUrl;
        a.download = 'chart.png';
        a.click();
      };
      img.src = url;
    };

    useEffect(() => {
      if (!data.length) return;

      const svg = d3.select(chartRef.current);
      const container = chartRef.current?.parentElement;
      if (!container) return;

      const renderChart = () => {
        if (!chartRef.current || !container) return;

        const containerWidth = container.offsetWidth;
        const margin = { top: 30, right: 30, bottom: 75, left: 80 };
        const width = containerWidth - margin.left - margin.right;
        const height = 400 - margin.top - margin.bottom;

        // Clear previous render
        svg.selectAll("*").remove();
        d3.select(container).selectAll(".tooltip").remove();

        svg
          .attr("viewBox", `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
          .attr("preserveAspectRatio", "xMidYMid meet");

        const g = svg
          .append("g")
          .attr("transform", `translate(${margin.left},${margin.top})`);

        // Aggregate data by fiscal year
        const dataByYear: any[] = [];
        data.reduce((res: Record<string, any>, d) => {
          if (!res[d.fiscal_year]) {
            res[d.fiscal_year] = { fiscal_year: d.fiscal_year, wages: 0, benefits: 0 };
            dataByYear.push(res[d.fiscal_year]);
          }
          res[d.fiscal_year].wages += d.wages;
          res[d.fiscal_year].benefits += d.benefits;
          return res;
        }, {});
        dataByYear.forEach(d => (d.total = d.wages + d.benefits));

        const keys: string[] = ["wages", "benefits"];

        // Scales
        const x = d3.scaleBand<string>()
          .domain(dataByYear.map(d => d.fiscal_year))
          .range([0, width])
          .padding(0.1);

        const y = d3.scaleLinear()
          .domain([0, d3.max(dataByYear, d => d.total) ?? 0])
          .range([height, 0]);

        const z = d3.scaleOrdinal<string>()
          .domain(keys)
          .range(["#2E8B8B", "#ffc63a"]);

        // Tooltip
        const tooltip = d3.select(container)
          .append("div")
          .attr("class", "tooltip")
          .style("position", "absolute")
          .style("visibility", "hidden")
          .style("background", "white")
          .style("border", "1px solid gray")
          .style("padding", "5px")
          .style("border-radius", "4px")
          .style("pointer-events", "none")
          .style("font-size", "12px")
          .style("z-index", "1000");

        // Legend
        const legend = svg.append("g")
          .attr("class", "legend")
          .attr("transform", `translate(${margin.left + 5},${margin.top})`);

        legend.append("text")
          .attr("transform", "rotate(-90)")
          .attr("x", -130)
          .attr("y", -80)
          .attr("dy", "1em")
          .style("text-anchor", "middle")
          .style("font-size", "12px")
          .text("Dollars($)");

        legend.selectAll("rect")
          .data(keys)
          .enter()
          .append("rect")
          .attr("x", (d, i) => i * 100)
          .attr("y", 0)
          .attr("width", 15)
          .attr("height", 15)
          .attr("fill", d => z(d) as string);

        legend.selectAll("text.label")
          .data(keys)
          .enter()
          .append("text")
          .attr("class", "label")
          .attr("x", (d, i) => i * 100 + 20)
          .attr("y", 12)
          .text(d => d.charAt(0).toUpperCase() + d.slice(1))
          .style("font-size", "12px")
          .attr("fill", "#000");

        // Stack generator
        const stackGenerator = d3.stack<YearData>().keys(keys);
        const stackSeries = stackGenerator(dataByYear);

        g.selectAll("g.layer")
          .data(stackSeries)
          .join("g")
          .attr("class", "layer")
          .attr("fill", d => z(d.key) as string)
          .selectAll("rect")
          .data(d => d)
          .join("rect")
          .attr("x", (d: any) => x(d.data.fiscal_year) ?? 0)
          .attr("y", (d: any) => y(d[1]))
          .attr("height", (d: any) => y(d[0]) - y(d[1]))
          .attr("width", x.bandwidth())
          .on("mouseover", (event: any, d: any) => {
            const parentDatum = d3.select<SVGGElement, any>(event.currentTarget.parentNode as SVGGElement).datum() as { key: string };
            tooltip
              .style("visibility", "visible")
              .style("opacity", "1")
              .html(`
                <strong>${d.data.fiscal_year}</strong><br/>
                ${parentDatum.key}: $${(d[1] - d[0]).toLocaleString()}
              `);
          })
          .on("mousemove", (event: any) => {
            const [x, y] = d3.pointer(event, container);
            tooltip.style("top", `${y - 30}px`).style("left", `${x + 10}px`);
          })
          .on("mouseout", () => {
            tooltip.style("visibility", "hidden");
          });

        // Axes
        g.append("g")
          .call(
            d3.axisLeft(y)
              .tickFormat(d3.format("$,~"))
          )
        g.append("g")
          .attr("transform", `translate(0,${height})`)
          .call(d3.axisBottom(x));
      };

      renderChart();

      const resizeObserver = new ResizeObserver(() => renderChart());
      if (chartRef.current?.parentElement) resizeObserver.observe(chartRef.current.parentElement);

      return () => resizeObserver.disconnect();
    }, [data]);

    return (
      <div id="chart-container" style={{ position: "relative" }}>
        <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
          <div
            className="btn btn-secondary"
            id="btn-chart-download"
            onClick={() => chartRef.current && handleD3ChartDownload(chartRef.current)}
            style={{ margin: 0 }}
          >
            Download
          </div>

          <div
            className="btn btn-secondary"
            id="btn-chart-fullscreen"
            data-toggle="modal"
            data-target="#fullscreen-modal"
            style={{ margin: 0 }}
          >
            <i className="fas fa-expand-arrows-alt"></i>
          </div>
        </div>

        {/* Chart SVG */}
        <svg ref={chartRef} style={{ width: "100%", height: "400px" }} />
      </div>
    );
  };

  useEffect(() => {
    getEmployeePayInfo();
  }, [])

  // New useMemo version
  const filteredDataMemo = useMemo(() => {
    return data.filter(item =>
      (selectedFilteredYears.length === 0 || selectedFilteredYears.includes(item.fiscal_year.toString())) &&
      (selectedFilteredEntities.length === 0 || selectedFilteredEntities.includes(item.entity_name)) &&
      (selectedFilteredEmployees.length === 0 || selectedFilteredEmployees.includes(item.employee_name)) &&
      (selectedFilteredTitles.length === 0 || selectedFilteredTitles.includes(item.title))
    );
  }, [data, selectedFilteredYears, selectedFilteredEntities, selectedFilteredEmployees, selectedFilteredTitles]);

  // Reset forms when filter year changes
  useEffect(() => {
    setSelectedFilteredYears([]);
    setSelectedFilteredEntities([]);
    setSelectedFilteredEmployees([]);
    setSelectedFilteredTitles([]);
  }, [filters]);

  const loadYearOptions = async (inputValue: string) => {
    const allYears = filters.filterYear.map(year => ({
      value: year.toString(),
      label: year.toString()
    }));

    return allYears.filter(i =>
      i.label.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  const loadEntityOptions = async (inputValue: string) => {
    const cleanInput = inputValue.toLowerCase().trim();

    const allEntities = filters.filterEntity.map(entity => ({
      value: entity,
      label: entity,
    }))

    return allEntities.filter(i =>
      i.label.toLowerCase().includes(cleanInput)
    );
  };

  const loadEmployeeOptions = async (inputValue: string) => {
    const cleanInput = inputValue.toLowerCase().trim();

    const allEmployees = filters.filterEmployee.map(employee => ({
      value: employee,
      label: employee,
    }))

    return allEmployees.filter(i => 
      i.label.toLowerCase().includes(cleanInput)
    );
  };

  const loadTitleOptions = async (inputValue: string) => {
    const cleanInput = inputValue.toLowerCase().trim();

    const allTitles = filters.filterTitle.map(title => ({
      value: title,
      label: title,
    }))

    return allTitles.filter(i =>
      i.label.toLowerCase().includes(cleanInput)
    );
  };

  return (
    <div className="employeePaySearch">
      <HeaderComponent/>
      <FeatureNav/>
      <BreadCrumbs/>
      <main>
        {loading ? <Loader /> : <></>}

        <div className="row">
          <div className="col-12" >
            <div className="jumbotron text-white mb-4 mx-4 pt-2 pb-2 px-0" style={{ backgroundColor: "var(--tu-teal)"}} id="jumbo-main">
              <div className="row px-4 py-2" id="header-container" >
                <div id="header-title-box" className="text-xs-center text-sm-center text-md-center text-lg-left text-xl-left"  >
                  <h1 style={{color: "white"}}>Employee Compensation Search
                    <button id="header-title-toggle-btn"
                        className="btn btn-grey300 btn-sm text-secondary"
                        type="button"
                        onClick={() => setShowHeaderInfo(!showHeaderInfo)}
                        aria-expanded={showHeaderInfo}
                        >
                        <i className="fas fa-info text-white"></i>
                    </button>    
                  </h1>
                </div>

                <Collapse in={showHeaderInfo}>
                  <div id="header-description" className="collapse py-2">
                    <p className="lead">
                      To search for employee wages and benefits, enter a name and click "Submit". The name submitted will
                      return partial matches on first and last names. Remember that "Tom" and "Thomas" are not the same
                      name and the employee search is performed on the legal name of the government employee. Also note
                      that searching for "Doug" as a last name will return multiple last names including "Doug",
                      "Dougall", "McDougall" and "MacDougall".
                    </p>
                    <p className="lead">
                      After clicking submit, use the filters to refine the search by fiscal year, entity, employee, and
                      job title. Both the Plot and Table will automatically update as filters are added/removed.
                    </p>
                  </div>
                </Collapse>
              </div>
            </div>
          </div>
        </div>

        {/* Init the Searchbar Section */}
        <div className="row px-4">
          <div className="col-12" >
            <div className="card mb-3">
                <div className="card-header text-white" style={{ backgroundColor: "var(--tu-teal)"}}>
                  <h2 className="text-white h3 text-capitalize">Search</h2>
                </div>
                <div className="card-body">
                  {/* Search Bar Input */}
                  <div className= "row">
                    <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-3 mt-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Employee Name Here"
                        value={searchName}
                        onChange={(event) => setSearchName(event.target.value)}
                        id="emp-search-input"
                        aria-describedby="empSearchHelp"
                      />
                    </div>
                    {/* Search Bar Submit Button */}
                    <div className="col-12 col-sm-6 col-md-4 col-lg-2 col-xl-2 mt-2 submit-button-container" >
                      <button
                        id="emp-submit-button"
                        type="button"
                        className="btn btn-secondary"
                        aria-label="Submit"
                        onClick={getEmployeePayInfo}
                      >
                        <i className="fas fa-paper-plane"></i>{" "}Submit
                      </button>
                    </div>
                  </div>
                </div>
            </div>
          </div>
        </div>

        {filteredDataMemo && filteredDataMemo.length > 0 && (
        <div className="row px-4" >
          <div className="col-12 h-100">
            <div className="card mt-4">
              <div className="card-header text-white" style={{ backgroundColor: "var(--tu-teal)"}}>
                <h2 className="text-white h3 text-capitalize">Filter</h2>
              </div>
              <div className="card-body px-0 mx-0 pt-0 ">
                <div id ="container-filter" className="row px-4 mt-2">
                  {/* Fiscal Year Filter */}
                  <div className="col-12 col-sm-12 col-md-6 col-lg-3 col-xl-3 mt-2 filter-year-input">
                    <h5><i className="far fa-calendar-alt"></i>{" "}Filter Year</h5>
                    <MultiSelectAsync
                      loadOptions={loadYearOptions}
                      value={selectedFilteredYears.map(year => ({ 
                        value: year.toString(), 
                        label: year.toString() 
                      }))}
                      onChange={(selectedOptions) => {
                        const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
                        setSelectedFilteredYears(values);
                      }}
                      isSearchable={true}
                      closeMenuOnSelect={false}
                      defaultOptions={filters.filterYear.map(year => ({ value: year.toString(), label: year.toString() }))}
                      options={filters.filterYear.map(year => ({ 
                        value: year.toString(), 
                        label: year.toString() 
                      }))}
                    />

                  </div>
                  {/* Entity Filter */}
                  <div className="col-12 col-sm-12 col-md-6 col-lg-3 col-xl-3 mt-2 filter-entity-input">
                    <h5><i className="fas fa-landmark"></i>&nbsp;&nbsp;Filter Entity</h5>
                    <MultiSelectAsync 
                      loadOptions={loadEntityOptions}
                      value={selectedFilteredEntities.map(entity => ({
                        value: entity,
                        label: entity,
                      }))}
                      onChange={(selectedOptions) => {
                        const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
                        setSelectedFilteredEntities(values);
                      }}
                      isSearchable={true}
                      closeMenuOnSelect={false}
                      defaultOptions={filters.filterEntity.map(entity => ({ value: entity, label: entity }))}
                      options={filters.filterEntity.map(entity => ({
                        value: entity,
                        label: entity
                      }))}
                    />
                  </div>

                  {/* Employee Filter */}
                  <div className="col-12 col-sm-12 col-md-6 col-lg-3 col-xl-3 mt-2 filter-employee-input">
                    <h5><i className="fas fa-user"></i>&nbsp;&nbsp;Filter Employee</h5>
                    <MultiSelectAsync 
                      loadOptions={loadEmployeeOptions}
                      value={selectedFilteredEmployees.map(employee => ({
                        value: employee,
                        label: employee,
                      }))}
                      onChange={(selectedOptions) => {
                        const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
                        setSelectedFilteredEmployees(values);
                      }}
                      isSearchable={true}
                      closeMenuOnSelect={false}
                      defaultOptions={filters.filterEmployee.map(employee => ({
                        value: employee, label: employee
                      }))}
                      options={filters.filterEmployee.map(employee => ({
                        value: employee,
                        label: employee
                      }))}
                    />
                  </div>

                  {/* Title Filter */}
                  <div className="col-12 col-sm-12 col-md-6 col-lg-3 col-xl-3 mt-2 filter-title-input">
                    <h5><i className="fas fa-briefcase"></i>&nbsp;&nbsp;Filter Title</h5>
                    <MultiSelectAsync 
                      loadOptions={loadTitleOptions}
                      value={selectedFilteredTitles.map(title => ({
                        value: title,
                        label: title,
                      }))}
                      onChange={(selectedOptions) => {
                        const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
                        setSelectedFilteredTitles(values);
                      }}
                      isSearchable={true}
                      closeMenuOnSelect={false}
                      defaultOptions={filters.filterTitle.map(title => ({ value: title, label: title }))}
                      options={filters.filterTitle.map(title => ({
                        value: title,
                        label: title,
                      }))}
                    />
                  </div>
                </div>
              </div>
            </div>  
          </div>
        </div>
        )}

        {/* Result Section */}
        {filteredDataMemo && filteredDataMemo.length > 0 && (
          <div className="row py-4 px-4">
          <div className="col-12 ">
            <div className="card bg-white">
              <div className="card-header text-white" style={{ backgroundColor: "var(--tu-teal)"}}>
                <h2 className="text-white h3 text-capitalize">Data</h2>
              </div>
              <div className="card-body">
                <div id="data-download-buttons-container" className="col-12">
                  <button id="download-csv" className="btn btn-secondary mb-2 mt-2" onClick={() => handleDownload("csv")}>CSV</button>
                  <button id="download-xlsx" className="btn btn-secondary mb-2 mt-2" onClick={() => handleDownload("xlsx")}>Excel</button>
                  <button id="download-html" className="btn btn-secondary mb-2 mt-2" onClick={() => handleDownload("html")}>HTML</button>
                  <button id="download-json" onClick={() => handleDownload("json")} className="btn btn-secondary mb-2 mt-2">JSON</button>
                </div>

                <div id="data-section" className="col-12">
                  <div id="tabulator-table" className="table-light">
                    <EmployeeTable ref={tabulatorRef} data={filteredDataMemo} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}
        {/* Result Section */}

        {/* Chart Section */}
        {filteredDataMemo && filteredDataMemo.length > 0 && (
          <div className="row py-4 px-4">
            <div className="col-12">
              <div className="card bg-white">
                {/* header */}
                <div
                  className="card-header text-white"
                  style={{ backgroundColor: "var(--tu-teal)" }}
                >
                  <h2 className="text-white h3 text-capitalize">Chart</h2>
                </div>

                {/* body */}
                <div className="card-body">
                  <div className="col-12" id="chart-container">
                    <div className="chart-spinner" id="chart-container-spinner" />
                    <div id="emp-comp-chart"></div>
                  </div>

                  {/* Chart */}
                  <EmployeeChart data={filteredDataMemo} />
                </div>
              </div>
            </div>
          </div>
        )}

        <ErrorModal
          show={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          title="No Results"
          message={error}
        />
      </main>
      <FooterSection />
    </div>
  )
}

export default EmployeeEmployeePaySearch;